import type {
  BandaAlcance,
  ColaboradorConAlcance,
  KpisAlcances,
  MiAlcance,
  MotivoTiendaSinAlcance,
  TramoAlcance,
} from '../types/metas.types';
import { veTodasLasTiendas } from './direccion';
import { esFechaIso, lunesDe, ms, sumarDias } from './semanas';

/**
 * Alcances (fase 5, artboard `Alcances`): venta real SIN IVA contra meta, en
 * semanas CERRADAS. Aquí sólo se da FORMA a lo que calcula la API; ningún
 * porcentaje se recalcula y nada se multiplica ni se divide por 1,16.
 */

/** Cuántas semanas cerradas ofrece el selector de la pestaña (un semestre). */
export const SEMANAS_CERRADAS_SELECTOR = 26;

/** Cuántas tiendas se ven antes de «Ver todas» (diseño: «Mostrando 8 de 12»). */
export const TIENDAS_A_LA_VISTA = 8;

/** La escala de la barra llega a 130 %: la marca de 100 % cae en el 76,9 % del ancho. */
export const ESCALA_BARRA = 1.3;
export const POSICION_MARCA_100 = (1 / ESCALA_BARRA) * 100;

/**
 * La última semana CERRADA según `hoy` (hora de México, de la API): la que
 * terminó el domingo anterior a la semana en curso. El lunes 28-sep ya cerró
 * la del 21 al 27.
 */
export function ultimaSemanaCerrada(hoy: string): string {
  return sumarDias(lunesDe(hoy), -7);
}

/** Las `n` últimas semanas cerradas, de la más vieja a la más reciente. */
export function semanasCerradas(hoy: string, n = SEMANAS_CERRADAS_SELECTOR): string[] {
  if (!esFechaIso(hoy)) return [];
  const ultima = ultimaSemanaCerrada(hoy);
  return Array.from({ length: n }, (_, i) => sumarDias(ultima, -7 * (n - 1 - i)));
}

/** ¿`lunes` es una semana que ya cerró? (un enlace viejo puede traer cualquier fecha). */
export function esSemanaCerrada(lunes: string | null | undefined, hoy: string): lunes is string {
  return esFechaIso(lunes) && esFechaIso(hoy) && ms(lunesDe(lunes)) <= ms(ultimaSemanaCerrada(hoy));
}

/**
 * `0.964` → «96.4 %» (una cifra decimal, formato mexicano); `null` → «—».
 *
 * ⚠️ Se TRUNCA, no se redondea: los cortes de banda son los del pago de
 * comisiones, y redondear escribía «86.0 %» a quien tiene 85,99 % —que no
 * cobra— y «100.0 %» a quien se quedó en 99,96 %. Truncado, el número nunca
 * contradice el color de su banda.
 */
export function pctAlcance(f: number | null | undefined): string {
  if (f === null || f === undefined || !Number.isFinite(f)) return '—';
  // La holgura absorbe el error binario (1,15 × 1000 = 1149,999…) sin cruzar un décimo real.
  const decimas = Math.floor(f * 1000 + 1e-6);
  return `${(decimas / 10).toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

/** Ancho de la barra de una tienda, en % de la pista (tope en 130 %). */
export function anchoBarra(f: number | null | undefined): number {
  if (f === null || f === undefined || !Number.isFinite(f) || f <= 0) return 0;
  return (Math.min(f, ESCALA_BARRA) / ESCALA_BARRA) * 100;
}

/**
 * Fondo de cada banda (tokens `metas-tramo-*`). Escritas completas: Tailwind
 * sólo genera las clases que encuentra literales en el código.
 */
export const CLASE_FONDO_BANDA: Record<BandaAlcance, string> = {
  bajo: 'bg-metas-tramo-bajo',
  medio: 'bg-metas-tramo-medio',
  alto: 'bg-metas-tramo-alto',
  max: 'bg-metas-tramo-max',
};

/** Las bandas de menor a mayor, como la barra apilada y la leyenda del diseño. */
export const ORDEN_BANDAS: BandaAlcance[] = ['bajo', 'medio', 'alto', 'max'];

const porciento = (x: number): number => Math.round(x * 1000) / 10;
const numPct = (x: number): string => String(porciento(x)).replace('.', ',');

/**
 * La etiqueta CORTA de una banda para la leyenda: «< 86 %», «86–99 %»,
 * «100–119 %», «≥ 120 %». Sale de los cortes que manda la API, no de valores
 * escritos aquí: si Comercial mueve un corte, la leyenda lo sigue.
 */
export function etiquetaCortaTramo(t: Pick<TramoAlcance, 'desde' | 'hasta' | 'etiqueta'>): string {
  const { desde, hasta } = t;
  if (desde === null && hasta !== null) return `< ${numPct(hasta)} %`;
  if (hasta === null && desde !== null) return `≥ ${numPct(desde)} %`;
  if (desde !== null && hasta !== null) {
    const a = porciento(desde);
    const b = porciento(hasta);
    return Number.isInteger(a) && Number.isInteger(b) ? `${a}–${b - 1} %` : `${numPct(desde)}–${numPct(hasta)} %`;
  }
  return t.etiqueta;
}

/**
 * El nombre accesible de la barra apilada (`role="img"`): las etiquetas
 * completas con su conteo, «Menos de 86 %: 14; 86 a 99 %: 32; …».
 */
export function ariaTramos(porBanda: KpisAlcances['porBanda']): string {
  return porBanda.map((b) => `${b.etiqueta}: ${b.n}`).join('; ');
}

/** El corte desde el que se está «en meta» (la banda `alto`); 100 % si la API no lo manda. */
export function corteEnMeta(tramos: TramoAlcance[]): number {
  return tramos.find((t) => t.banda === 'alto')?.desde ?? 1;
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * `ventaAl` de la API (ya en hora de México, `AAAA-MM-DDTHH:MM:SS`, sin zona)
 * → «28 sep 06:00». Se lee campo por campo: pasarlo por `Date` lo
 * reinterpretaría en la zona del navegador.
 */
export function ventaAlTexto(v: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(v ?? '');
  if (!m) return null;
  const mes = MESES[Number(m[2]) - 1];
  if (!mes) return null;
  return `${Number(m[3])} ${mes} ${m[4]}:${m[5]}`;
}

/** Por qué una tienda no tiene alcance, para su pastilla gris. */
export function motivoTiendaTexto(m: MotivoTiendaSinAlcance): string | null {
  switch (m) {
    case 'sin_meta_tienda':
      return 'Sin meta de tienda';
    case 'meta_incompleta':
      return 'Meta de tienda incompleta';
    case 'meta_con_iva':
      return 'Meta de tienda con IVA';
    default:
      return null;
  }
}

/**
 * La etiqueta CORTA de la lista de tiendas (F5-V5): la columna % es angosta y
 * la pastilla va en una línea. La larga va en el `title` y para el lector.
 */
export function motivoTiendaCorto(m: MotivoTiendaSinAlcance): string | null {
  switch (m) {
    case 'sin_meta_tienda':
      return 'Sin meta';
    case 'meta_incompleta':
      return 'Incompleta';
    case 'meta_con_iva':
      return 'Con IVA';
    default:
      return null;
  }
}

/** La explicación larga, para el lector y el `title` de la pastilla. */
export function motivoTiendaDetalle(m: MotivoTiendaSinAlcance): string | null {
  switch (m) {
    case 'sin_meta_tienda':
      return 'La fuente de metas no tiene meta de tienda para esta semana: no se calcula su alcance ni entra al del regional.';
    case 'meta_incompleta':
      return 'La meta de tienda no cubre los 7 días de la semana: no se calcula su alcance ni entra al del regional.';
    case 'meta_con_iva':
      return 'La meta de tienda viene con IVA y la venta es sin IVA: no se comparan. Se corrige en la fuente de metas.';
    default:
      return null;
  }
}

/**
 * La pastilla de un colaborador SIN % (corta), a partir del motivo que da la
 * API en palabras. El motivo completo va en el `title` y para el lector.
 *
 *   «Sin meta autorizada: la tienda está enviada…» → «Sin autorizar»
 *   «Sin clave: no tiene agente en el ERP…»       → «Sin clave»
 *   «Su puesto no lleva meta individual (CAJERO).» → «Sin meta por puesto»
 *   «Vacaciones: del 22 al 26»                     → «Vacaciones»
 */
export function motivoCorto(c: Pick<ColaboradorConAlcance, 'pct' | 'motivo' | 'sinClave'> & { esCasa?: boolean }): string | null {
  if (c.pct !== null && c.pct !== undefined) return null;
  // La Casa (ronda F, 23-oct-2026): no es una persona; su venta ya cuenta en la de la tienda.
  if (c.esCasa) return 'Nadie la cobra';
  const m = (c.motivo ?? '').trim();
  if (!m) return c.sinClave ? 'Sin clave' : '—';
  if (/^Sin meta autorizada/i.test(m)) return 'Sin autorizar';
  if (/^Sin clave/i.test(m)) return 'Sin clave';
  if (/^Su puesto no lleva meta/i.test(m)) return 'Sin meta por puesto';
  if (/^Sin meta \(opcional/i.test(m)) return 'Sin meta (opcional)';
  if (/^Venta de la semana incompleta/i.test(m)) return 'Venta incompleta';
  const antes = m.split(':')[0].replace(/\.$/, '').trim();
  return antes || m;
}

/** Cadenas del filtro, como las nombra el diseño. */
export const CADENAS_ALCANCES: { valor: string; etiqueta: string }[] = [
  { valor: 'VAZ', etiqueta: 'Vazza' },
  { valor: 'TAF', etiqueta: 'Flexi' },
  { valor: 'MRH', etiqueta: 'Marcha' },
  { valor: 'TRE', etiqueta: 'Trender' },
];

/** Las cadenas que la cuenta ve, en el orden del diseño (las desconocidas, al final). */
export function cadenasDe(tiendas: { chain: string }[]): { valor: string; etiqueta: string }[] {
  const hay = new Set(tiendas.map((t) => t.chain));
  const conocidas = CADENAS_ALCANCES.filter((c) => hay.has(c.valor));
  const otras = Array.from(hay)
    .filter((c) => !CADENAS_ALCANCES.some((x) => x.valor === c))
    .sort()
    .map((c) => ({ valor: c, etiqueta: c }));
  return [...conocidas, ...otras];
}

/**
 * El nombre del primer indicador según quién mira: «Alcance del regional»
 * (el regional, o el administrador o dirección filtrando uno), «Alcance de tus
 * tiendas» (el supervisor) o «Alcance de las tiendas» (el administrador o
 * dirección sin filtro: ven todo el catálogo, no «sus» tiendas).
 * Decir «del regional» a un supervisor de 3 tiendas sería decir algo falso.
 * Con el filtro de supervisor (21-oct-2026) el indicador es de sus tiendas:
 * «Alcance del supervisor», aunque mire el regional.
 */
export function etiquetaAlcanceGlobal(
  alcance: Pick<MiAlcance, 'tipo' | 'esAdmin'> & Partial<Pick<MiAlcance, 'esDireccion' | 'alcances'>>,
  regionalFiltro: string,
  supervisorFiltro = '',
): string {
  if (supervisorFiltro) return 'Alcance del supervisor';
  if (regionalFiltro || alcance.tipo === 'regional') return 'Alcance del regional';
  if (veTodasLasTiendas({ esAdmin: alcance.esAdmin, esDireccion: alcance.esDireccion, alcances: alcance.alcances ?? [] }))
    return 'Alcance de las tiendas';
  return 'Alcance de tus tiendas';
}
